import { Component, NgZone, OnInit } from '@angular/core';
import {
  ApexAnnotations,
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule,
} from 'ng-apexcharts';
import {
  ApiService,
  TeamSkillData,
  TeamSkillEntry,
  UserKnowledge,
} from '../../services/api.service';
import { NgClass } from '@angular/common';
import { MultiSelect } from 'primeng/multiselect';
import { SkillmapPanelComponent } from '../skillmap-panel/skillmap-panel.component';
import { TableService } from '../../services/table.service';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from 'primeng/button';
import { PrimeTemplate } from 'primeng/api';
import { ProgressSpinner } from 'primeng/progressspinner';

@Component({
  selector: 'app-skillmap',
  standalone: true,
  imports: [
    NgApexchartsModule,
    NgClass,
    MultiSelect,
    SkillmapPanelComponent,
    FormsModule,
    ButtonDirective,
    PrimeTemplate,
    ProgressSpinner,
  ],
  templateUrl: './skillmap.component.html',
  styleUrls: ['./skillmap.component.scss'],
})
export class SkillmapComponent implements OnInit {
  chartOptions: {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    dataLabels: ApexDataLabels;
    title: ApexTitleSubtitle;
    annotations: ApexAnnotations;
    tooltip: ApexTooltip;
  } = {} as any;

  userKnowledgeData!: UserKnowledge[];
  showPanel = false;
  selectedDataLabel = '';
  teamSkillEntry?: TeamSkillEntry;
  categories: string[] = [];
  selectedCategories: string[] = [];
  private skillToTeamsMap?: TeamSkillEntry[] | undefined;

  labelSize: number = 14;

  constructor(
    private apiService: ApiService,
    private tableService: TableService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.loadCategories();
    this.loadChartData();
  }

  showPanelIsFalse() {
    this.showPanel = false;
  }

  private loadCategories(): void {
    this.tableService.getSkills().subscribe(skills => {
      const uniqueCategories = new Set(skills.map(s => s.g_name));
      this.categories = Array.from(uniqueCategories);
    });
  }

  protected loadChartData(): void {
    this.apiService.getAll().subscribe(allSkillsData => {
      this.tableService.getSkills().subscribe(skillMeta => {
        const filteredSkillNames = skillMeta
          .filter(
            skill =>
              this.selectedCategories.length === 0 ||
              this.selectedCategories.includes(skill.g_name)
          )
          .map(skill => skill.skill_name);

        const filteredData: Record<string, { conosco: number; tot: number }> =
          {};
        for (const key of filteredSkillNames) {
          if (allSkillsData[key]) {
            filteredData[key] = allSkillsData[key];
          }
        }

        this.apiService
          .getSkillTeamMap()
          .subscribe((teamSkillData: TeamSkillData) => {
            this.skillToTeamsMap = teamSkillData.data;
            const totalActiveTeams = teamSkillData.activeTeamCount;

            const series = this.buildChartSeries(
              filteredData,
              this.mapSkillsToTeams(teamSkillData.data),
              totalActiveTeams
            );
            this.setupChart(series);
          });
      });
    });
  }

  private mapSkillsToTeams(
    data: TeamSkillData['data']
  ): Record<string, Set<string>> {
    const map: Record<string, Set<string>> = {};

    for (const { skill, teams } of data) {
      map[skill] = new Set(teams);
    }

    return map;
  }

  private buildChartSeries(
    allSkillsData: Record<string, { conosco: number; tot: number }>,
    skillToTeamsMap: Record<string, Set<string>>,
    totalActiveTeams: number
  ): any[] {
    return Object.keys(allSkillsData).map(skillName => {
      const { conosco = 0, tot = 1 } = allSkillsData[skillName] ?? {};
      const teamCount = skillToTeamsMap[skillName]?.size ?? 0;

      return {
        x: (conosco / tot) * 100,
        y: (teamCount / totalActiveTeams) * 100 + Math.random(), // Adding a small offset to avoid overlap
        meta: { label: skillName },
      };
    });
  }

  private setupChart(series: any[]): void {
    this.chartOptions = {
      series: [
        {
          name: 'Skill Level',
          data: series,
        },
      ],
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        width: '100%',
        height: '100%',
        type: 'scatter',
        events: {
          dataPointSelection: (event, chartContext, config) => {
            const selected =
              config.w.config.series[config.seriesIndex].data[
                config.dataPointIndex
              ];
            this.handlePointSelection(selected.meta.label);
          },
        },
      },
      dataLabels: {
        style: {
          colors: ['#e2effeff'],
          fontSize: '10px',
        },
        background: {
          padding: 12,
          foreColor: '#000',
          borderRadius: 4,
        },
        enabled: true,
        formatter: (_val, opt) =>
          opt.w.globals.initialSeries[0].data[opt.dataPointIndex].meta.label,
      },
      xaxis: {
        min: 0,
        max: 100,
        tickAmount: 10,
        labels: {
          formatter: val => `${Math.round(Number(val))}`,
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        tickAmount: 10,
        labels: {
          formatter: val => `${Math.round(val)}`,
        },
      },
      title: {
        text: 'Skillmap Chart',
      },
      annotations: {
        xaxis: [
          {
            x: 50,
            borderColor: '#000',
            borderWidth: 2,
            strokeDashArray: 2,
            label: { text: 'Projects' },
          },
        ],
        yaxis: [
          {
            y: 50,
            borderColor: '#000',
            borderWidth: 2,
            strokeDashArray: 2,
            label: { text: 'People' },
          },
        ],
      },
      tooltip: {
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const point = w.config.series[seriesIndex].data[dataPointIndex];
          return `
          <div class="tooltip">
            <p><strong>${point.meta.label}</strong></p>
            <p>People <strong>${Math.round(point.x)}%</strong></p>
            <p>Projects <strong>${Math.round(point.y)}%</strong></p>
          </div>`;
        },
      },
    };
  }

  private handlePointSelection(skillName: string): void {
    this.ngZone.run(() => {
      this.showPanel = true;

      this.teamSkillEntry = this.skillToTeamsMap?.find(
        st => st.skill == skillName
      );

      this.selectedDataLabel = skillName;
      this.apiService.getSkillUserKnowledge(skillName).subscribe(data => {
        this.userKnowledgeData = data;
      });
    });
  }

  increaseLabelSize() {
    if (this.labelSize < 20) {
      this.labelSize++;
      this.chartOptions = {
        ...this.chartOptions,
        dataLabels: {
          ...this.chartOptions.dataLabels,
          style: {
            ...this.chartOptions.dataLabels.style,
            fontSize: `${this.labelSize}px`,
          },
        },
      };
    }
  }

  decreaseLabelSize() {
    if (this.labelSize > 10) {
      this.labelSize--;
      this.chartOptions = {
        ...this.chartOptions,
        dataLabels: {
          ...this.chartOptions.dataLabels,
          style: {
            ...this.chartOptions.dataLabels.style,
            fontSize: `${this.labelSize}px`,
          },
        },
      };
    }
  }
}
